import { useState } from 'react';

const projectTypes = [
  'Asbestos Abatement',
  'Hazardous Material Removal',
  'Mold Remediation',
  'Lead-Based Paint Removal',
  'Disaster Restoration',
  'Demolition & Site Cleanup',
  'Environmental Consulting',
  'Emergency Response',
  'Other',
];

export default function FinalCTASection() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const fields = form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
      'input, select, textarea'
    );
    fields.forEach((field) => {
      if (field.name) data.append(field.name, field.value);
    });

    setSubmitting(true);
    try {
      await fetch('https://readdy.ai/api/form/d70jlp6th28qd804csb0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="assessment" className="relative bg-ecos-charcoal py-0 overflow-hidden">

      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-18"
        style={{
          backgroundImage:
            "url('https://public.readdy.ai/ai/img_res/78070465b3fc40404d5bfcf455215f66.jpg')",
        }}
      ></div>
      <div className="absolute inset-0 bg-gradient-to-b from-ecos-charcoal/60 via-transparent to-ecos-charcoal/60"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">

          {/* Left — Messaging */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-2 h-2 bg-ecos-green rounded-full"></div>
              <span className="text-ecos-green text-xs uppercase tracking-widest font-semibold">Get Started Today</span>
            </div>
            <h2 className="font-serif text-white text-4xl lg:text-5xl font-semibold leading-tight mb-6">
              Get a Certified Environmental Assessment Today
            </h2>
            <p className="text-white/55 text-base leading-relaxed mb-8">
              Speak with a licensed environmental specialist. We provide accurate, project-specific assessments and full compliance planning — with 24/7 emergency response when you need it most.
            </p>

            {/* Quick contact options */}
            <div className="space-y-4">
              <a
                href="tel:+18888685420"
                className="flex items-center gap-4 bg-ecos-green hover:bg-ecos-green-light px-6 py-4 rounded-sm transition-colors cursor-pointer group"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-white/15 rounded-sm flex-shrink-0">
                  <i className="ri-phone-fill text-white text-xl"></i>
                </div>
                <div>
                  <p className="text-white font-semibold text-base">888-868-5420</p>
                  <p className="text-white/70 text-xs">24/7 Emergency Line Available</p>
                </div>
                <div className="ml-auto w-6 h-6 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-white/50 group-hover:text-white transition-colors"></i>
                </div>
              </a>

              <a
                href="mailto:info@ecosenvironmental.com"
                className="flex items-center gap-4 border border-white/15 hover:border-white/30 px-6 py-4 rounded-sm transition-colors cursor-pointer group"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-white/8 rounded-sm flex-shrink-0">
                  <i className="ri-mail-line text-white/70 text-xl"></i>
                </div>
                <div>
                  <p className="text-white/80 font-medium text-sm">info@ecosenvironmental.com</p>
                  <p className="text-white/40 text-xs">Typically responds within 4 hours</p>
                </div>
              </a>
            </div>

            {/* Trust signals */}
            <div className="mt-8 pt-8 border-t border-white/10 grid grid-cols-3 gap-4">
              {[
                { icon: 'ri-shield-check-line', text: 'Licensed & Insured' },
                { icon: 'ri-time-line', text: '24/7 Emergency' },
                { icon: 'ri-medal-line', text: 'EPA / OSHA Certified' },
              ].map((item) => (
                <div key={item.text} className="flex flex-col items-center text-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className={`${item.icon} text-ecos-green text-xl`}></i>
                  </div>
                  <span className="text-white/50 text-xs">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Form */}
          <div className="lg:col-span-7">
            {submitted ? (
              <div className="bg-ecos-charcoal-light border border-ecos-green/30 rounded-sm p-14 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-ecos-green/20 rounded-full mx-auto mb-5">
                  <i className="ri-checkbox-circle-line text-ecos-green text-3xl"></i>
                </div>
                <h3 className="font-serif text-white text-2xl font-semibold mb-3">Assessment Request Received</h3>
                <p className="text-white/55 text-base max-w-sm mx-auto">
                  A certified specialist will contact you within 4 business hours to discuss your assessment. For emergencies, call 888-868-5420 immediately.
                </p>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-ecos-charcoal-light border border-white/10 rounded-sm p-8 lg:p-10"
              >
                <p className="text-white font-semibold text-lg mb-6">Request Your Free Assessment</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-white/55 text-xs uppercase tracking-wider mb-2 font-medium">
                      Full Name *
                    </label>
                    <input
                      name="name"
                      type="text"
                      required
                      placeholder="Your full name"
                      className="w-full bg-ecos-charcoal border border-white/12 hover:border-white/25 focus:border-ecos-green text-white placeholder-white/25 text-sm px-4 py-3.5 rounded-sm outline-none transition-colors font-sans"
                    />
                  </div>
                  <div>
                    <label className="block text-white/55 text-xs uppercase tracking-wider mb-2 font-medium">
                      Phone Number *
                    </label>
                    <input
                      name="phone"
                      type="tel"
                      required
                      placeholder="(xxx) xxx-xxxx"
                      className="w-full bg-ecos-charcoal border border-white/12 hover:border-white/25 focus:border-ecos-green text-white placeholder-white/25 text-sm px-4 py-3.5 rounded-sm outline-none transition-colors font-sans"
                    />
                  </div>
                  <div>
                    <label className="block text-white/55 text-xs uppercase tracking-wider mb-2 font-medium">
                      Email Address *
                    </label>
                    <input
                      name="email"
                      type="email"
                      required
                      placeholder="your@email.com"
                      className="w-full bg-ecos-charcoal border border-white/12 hover:border-white/25 focus:border-ecos-green text-white placeholder-white/25 text-sm px-4 py-3.5 rounded-sm outline-none transition-colors font-sans"
                    />
                  </div>
                  <div>
                    <label className="block text-white/55 text-xs uppercase tracking-wider mb-2 font-medium">
                      Project Type *
                    </label>
                    <div className="relative">
                      <select
                        name="project_type"
                        required
                        className="w-full bg-ecos-charcoal border border-white/12 hover:border-white/25 focus:border-ecos-green text-white text-sm px-4 py-3.5 rounded-sm outline-none transition-colors appearance-none cursor-pointer font-sans"
                      >
                        <option value="" className="bg-ecos-charcoal">Select project type</option>
                        {projectTypes.map((t) => (
                          <option key={t} value={t} className="bg-ecos-charcoal">{t}</option>
                        ))}
                      </select>
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                        <i className="ri-arrow-down-s-line text-white/35"></i>
                      </div>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-white/55 text-xs uppercase tracking-wider mb-2 font-medium">
                      Project Location / Address *
                    </label>
                    <input
                      name="location"
                      type="text"
                      required
                      placeholder="City, State or full address"
                      className="w-full bg-ecos-charcoal border border-white/12 hover:border-white/25 focus:border-ecos-green text-white placeholder-white/25 text-sm px-4 py-3.5 rounded-sm outline-none transition-colors font-sans"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-white/55 text-xs uppercase tracking-wider mb-2 font-medium">
                      Additional Details
                    </label>
                    <textarea
                      name="details"
                      rows={4}
                      maxLength={500}
                      placeholder="Describe your project, urgency, or any known concerns..."
                      className="w-full bg-ecos-charcoal border border-white/12 hover:border-white/25 focus:border-ecos-green text-white placeholder-white/25 text-sm px-4 py-3.5 rounded-sm outline-none transition-colors resize-none font-sans"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="mt-6 w-full bg-ecos-green hover:bg-ecos-green-light text-white font-semibold text-sm py-4 rounded-sm transition-colors duration-200 cursor-pointer whitespace-nowrap tracking-wide disabled:opacity-60"
                >
                  {submitting ? 'Submitting...' : 'Request Certified Assessment — Free'}
                </button>
                <p className="text-white/25 text-xs text-center mt-4">
                  Licensed professionals respond within 4 hours. All inquiries are confidential.
                </p>
              </form>
            )}
          </div>

        </div>
      </div>
    </section>
  );
}
